import { CancellationToken, IHttpApi, IHttpApiFetchConfig } from '@cian/http-api';
import { BaseQueryFn } from '@reduxjs/toolkit/query/react';
import { TMetaDeepJson } from '@cian/logger/shared';
import { HttpBadStatusCodeError } from '@cian/peperrors/shared';

import { IApplicationContext } from '../../../types/ICustomContextParams';
import { extractStoreStateForRequestLog } from '../extractStoreStateForRequestLog';

interface IFetchFunctionParams<TParams = unknown> {
  httpApi: IHttpApi;
  config?: IHttpApiFetchConfig;
  parameters?: TParams;
}

interface IFetchFunctionOptions<TParams = unknown, TResponse = unknown> {
  fetchFunction: (params: IFetchFunctionParams<TParams>) => Promise<TResponse>;
  parameters?: TParams;
  config?: IHttpApiFetchConfig;
  loggerInfo: {
    description: string;
    degradation?: string;
    safeParameters?: TMetaDeepJson;
  };
}
/**
 Нормализует ответ от новой версии API
 Обрабатывает: FetchResponse<T> | MappedResponse
 */
function extractResponseData<T>(response: T): {
  statusCode: number;
  data: unknown;
  headers?: Headers;
} {
  // 1. FetchResponse - берем реальный статус
  if (response && typeof response === 'object' && 'statusCode' in response && 'response' in response) {
    const fetchResponse = response as {
      statusCode: number;
      response: unknown;
      headers?: Headers;
    };

    return {
      statusCode: fetchResponse.statusCode,
      data: fetchResponse.response,
      headers: fetchResponse.headers,
    };
  }
  // 2. Mapped Response
  const data = response as Record<string, never>;
  const errorIndicators = ['errorCode', 'error', 'message', 'code', 'validationErrors', 'errors', 'errorMessage'];
  const hasErrorIndicator = errorIndicators.some(key => key in data || data[key] !== undefined);

  if (hasErrorIndicator) {
    return {
      statusCode: 400,
      data,
    };
  }

  return {
    statusCode: 200,
    data,
  };
}

export const createBaseQuery = ({
  httpApi,
  logger,
  custom,
}: IApplicationContext): BaseQueryFn<IFetchFunctionOptions, unknown, unknown> => {
  return async ({ fetchFunction, parameters, loggerInfo, config }, baseQueryApi) => {
    const domain = baseQueryApi?.endpoint ?? 'unknown-request';
    const cancellationToken = new CancellationToken();
    const cancelRequestCallback = () => cancellationToken.cancel();
    baseQueryApi?.signal.addEventListener('abort', cancelRequestCallback);
    try {
      const response = await fetchFunction({
        httpApi,
        parameters,
        config: {
          ...config,
          cancellationToken,
        },
      });

      const { statusCode, data } = extractResponseData(response);

      if (statusCode === 200) {
        return { data };
      }

      logger[statusCode === 400 ? 'warning' : 'error'](
        new HttpBadStatusCodeError({
          message: `Запрос ${domain} ответил с кодом ${statusCode}`,
          domain,
          statusCode,
        }),
        {
          description: loggerInfo.description,
          degradation: loggerInfo.degradation,
          userId: custom?.userId,
          parameters: JSON.stringify(loggerInfo.safeParameters),
          storeState: extractStoreStateForRequestLog(baseQueryApi?.getState()),
          response: JSON.stringify(response),
        },
      );

      return { error: data };
    } catch (error) {
      logger.error(error as Error, {
        domain,
        description: loggerInfo.description,
        degradation: loggerInfo.degradation,
        userId: custom?.userId,
        parameters: JSON.stringify(loggerInfo.safeParameters),
        storeState: extractStoreStateForRequestLog(baseQueryApi?.getState()),
      });

      return { error: 'UNKNOWN_REQUEST_REJECTION' };
    } finally {
      baseQueryApi?.signal.removeEventListener('abort', cancelRequestCallback);
    }
  };
};
